import { Placeholder, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { Forms } from 'lib/templates/Feature.Dart.model';
import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { rewritePathByHost } from 'lib/utils/rewrite-helper';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import ReCAPTCHA from 'react-google-recaptcha';
import GetLanguageCode from 'helpers/GetLanguageCode/GetLanguageCode';
import config from 'temp/config';
import { tailwindVariants } from './SCJBrandsContactFormTailwind';
import GoogleMaterialSymbol from 'helpers/GoogleMaterialSymbol/GoogleMaterialSymbol';
import { useI18n } from 'next-localization';

export type SCJBrandsContactFormProps = ComponentProps &
  Forms.ScjbrandsContact.ScjBrandsContactForm;
const nameLabelMappings: Record<string, string> = {};

interface FormFieldProps {
  label?: string;
  required?: boolean;
  show?: boolean;
  data?: Forms.ScjbrandsContact.SelectListItem[];
  name: string;
  type?: 'text' | 'select' | 'checkbox' | 'textarea';
  fristOptionText?: string;
  error?: string;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  onInput?: (
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  required = false,
  show = true,
  data,
  name,
  type = 'text',
  error,
  onBlur,
  onInput,
}) => {
  const { sitecoreContext } = useSitecoreContext();

  if (!show) return null;

  const rtlAlignment = sitecoreContext?.enableRTL === '1' ? 'rtl' : 'ltr';

  const {
    formInputBox,
    formInputBoxTextArea,
    formSelectWrapper,
    inputLabel,
    inputSelect,
    inputCheckbox,
    arrowDropDown,
    requiredColor,
    formFieldWrapper,
    errorMessageText,
  } = tailwindVariants({
    rtlAlignment: rtlAlignment as 'ltr' | 'rtl',
  });

  const fieldId = `field-${name}`;
  nameLabelMappings[name] = label ?? '';

  const getInputProps = () => {
    const baseProps = {
      id: fieldId,
      name,
      required,
      className: `${type !== 'textarea' ? formInputBox() : formInputBoxTextArea()} ${
        error && 'border border-color-system-alert-400'
      }`,
      Placeholder,
      onBlur,
      onInput,
    };

    if (type === 'text' || type === 'textarea') {
      if (name === 'Zip_Postal_Code') {
        return {
          ...baseProps,
          inputMode: 'text' as const, // this can be letters in other countries
          autoComplete: 'postal-code',
          maxLength: 9,
        };
      }

      if (name === 'Email' || name === 'Verify_Email') {
        return {
          ...baseProps,
          type: 'email' as const,
          autoComplete: name === 'Email' ? 'email' : 'off',
        };
      }

      if (name === 'Phone') {
        return {
          ...baseProps,
          inputMode: 'tel' as const,
          autoComplete: 'tel',
          pattern: '[0-9]*',
          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
            const allowedKeys = [
              'Backspace',
              'Delete',
              'ArrowLeft',
              'ArrowRight',
              'Tab',
              'Home',
              'End',
            ];

            if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
              e.preventDefault();
            }
          },
        };
      }

      return {
        ...baseProps,
        type: 'text' as const,
      };
    }

    if (type === 'select') {
      return baseProps;
    }

    if (type === 'checkbox') {
      return {
        ...baseProps,
        type: 'checkbox' as const,
        className: 'h-4 w-4',
      };
    }

    return baseProps;
  };

  const renderField = () => {
    switch (type) {
      case 'checkbox':
        return (
          <div className="flex space-x-2">
            <input {...getInputProps()} />
            {label && (
              <label
                htmlFor={fieldId}
                className={inputCheckbox()}
                dangerouslySetInnerHTML={{
                  __html:
                    (required ? '<span class="text-color-system-alert-400">*</span> ' : '') +
                    (label ?? ''),
                }}
              />
            )}
          </div>
        );
      case 'select':
        return (
          <>
            {label && (
              <label htmlFor={fieldId} className={inputLabel()}>
                {required && <span className={requiredColor()}>* </span>}
                {label}
              </label>
            )}
            <div className={formSelectWrapper()}>
              <select {...(getInputProps() as React.SelectHTMLAttributes<HTMLSelectElement>)}>
                {data?.map((option, index) => (
                  <option
                    key={index}
                    value={option.fields?.value?.value}
                    disabled={option.fields?.disabled?.value}
                    className="!appearance-none"
                  >
                    {option.fields?.name?.value}
                  </option>
                ))}
              </select>
              {/* Custom dropdown arrow  */}
              <div className={inputSelect()}>
                <GoogleMaterialSymbol className={arrowDropDown()} icon="arrow_drop_down" />
              </div>
            </div>
          </>
        );
      case 'textarea':
        return (
          <>
            {label && (
              <label htmlFor={fieldId} className={inputLabel()}>
                {required && <span className={requiredColor()}>* </span>}
                {label}
              </label>
            )}
            <textarea
              {...(getInputProps() as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
              rows={5}
            />
          </>
        );
      default:
        return (
          <>
            {label && (
              <label htmlFor={fieldId} className={inputLabel()}>
                {required && <span className={requiredColor()}>* </span>}
                {label}
              </label>
            )}

            <input {...getInputProps()} placeholder={label} />
          </>
        );
    }
  };

  return (
    <div className={formFieldWrapper()}>
      {renderField()}
      {error && <p className={errorMessageText()}>{error}</p>}
    </div>
  );
};

export const SCJBrandsContactForm = (props: SCJBrandsContactFormProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const recaptchaRef = useRef(null);
  const router = useRouter();
  const formRef = useRef<HTMLFormElement>(null);
  const [isValid, setIsValid] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [regardingValue, setRegardingValue] = useState('');
  const [subjectValue, setSubjectValue] = useState('');
  const [currentUrl, setCurrentUrl] = useState('');
  const current_lang = GetLanguageCode(sitecoreContext?.language);
  const upcHelpText = props?.fields?.productUpcHelp;
  const batchHelpText = props?.fields?.batchCodeHelp;
  const rtlAlignment = sitecoreContext?.enableRTL === '1' ? 'rtl' : 'ltr';
  const i18n = useI18n();

  const {
    base,
    formWrapper,
    formHeader,
    horizontalBar,
    formIntroduction,
    formContainerTop,
    formContainerBottom,
    formContainerOneColumn,
    formContainerTwoColumn,
    negativeSpace,
    requiredColor,
    requiredLabel,
    UpcHelpImage,
    BatchHelpImage,
    helpBoxRTE,
    errorMessageText,
    submitButton,
    submitButtonInvalid,
  } = tailwindVariants({
    rtlAlignment: rtlAlignment as 'ltr' | 'rtl',
  });

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setCurrentUrl(document.location.href);
    }
  }, [currentUrl]);

  useEffect(() => {
    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    let is_valid = props.fields?.moneyBackGuaranteeAllowedHosts?.length === 0;
    props.fields?.moneyBackGuaranteeAllowedHosts?.forEach((e) => {
      const domain = e.fields?.domain?.value?.toLowerCase() ?? '';
      if (referrer.toLowerCase().includes(domain)) {
        is_valid = true;
      }
    });
    if (!is_valid) {
      router.replace('/not-found'); // page shouldn't exist in CMS!
    }
  }, [props.fields, router]);

  function getFormattedDateTime() {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');

    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());

    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const isRequiredText = i18n.t('requiredText')
    ? i18n.t('requiredText')
    : 'This field is required.';

  const validateField = (name: string, value: string, isRequired: boolean): string => {
    if (isRequired && !value.trim()) {
      return isRequiredText;
    }

    if ((name === 'Email' || name === 'Verify_Email') && value) {
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (!isValidEmail) {
        return props.fields?.emailNotValidErrorMessage?.value || 'Invalid email address';
      }
    }

    return '';
  };

  const handleInput = (
    e: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

    if (target.name === 'Regarding') {
      setRegardingValue(target.value);
    }

    if (target.name === 'Subject') {
      setSubjectValue(target.value);
      // Clear UPC error if Subject change makes it non-required
      const upcRequired =
        props.fields?.productUpcRequired?.value ||
        target.value === props.fields?.productUpcMakeRequiredOnSubjectValue?.fields?.value?.value;
      if (!upcRequired && formErrors.UPC) {
        const updatedErrors = { ...formErrors };
        delete updatedErrors.UPC;
        setFormErrors(updatedErrors);
      }
    }

    if (formRef.current) {
      setIsValid(formRef.current.checkValidity());
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, required } = e.target;
    const form = formRef.current;
    const updatedErrors = { ...formErrors };

    if (e.target.type === 'checkbox' && required) {
      const input = e.target as HTMLInputElement;
      if (!input.checked) {
        updatedErrors[name] = 'This field is required.';
      } else {
        delete updatedErrors[name];
      }
      setFormErrors(updatedErrors);
      return;
    }

    const fieldError = validateField(name, value, required);
    if (fieldError) {
      updatedErrors[name] = fieldError;
    } else {
      delete updatedErrors[name];
    }

    if ((name === 'Email' || name === 'Verify_Email') && form) {
      const email = form.Email?.value;
      const verifyEmail = form.Verify_Email?.value;
      if (email && verifyEmail && email !== verifyEmail) {
        updatedErrors.Verify_Email =
          props.fields?.emailsDoNotMatchErrorMessage?.value || 'Emails do not match';
      } else {
        delete updatedErrors.Verify_Email;
      }
    }

    setFormErrors(updatedErrors);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = form.Email?.value;
    const verifyEmail = form.Verify_Email?.value;
    const formData: Record<string, string | boolean> = {};
    const newErrors: Record<string, string> = {};

    for (const element of form.elements) {
      if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement
      ) {
        if (element instanceof HTMLInputElement && element.type === 'checkbox') {
          formData[element.name] = element.checked;
        } else {
          formData[element.name] = element.value;
        }

        const error = validateField(element.name, element.value, element.required);
        if (error) newErrors[element.name] = error;
      }
    }

    if (email !== verifyEmail) {
      newErrors.Verify_Email =
        props.fields?.emailsDoNotMatchErrorMessage?.value || 'Emails do not match';
    }

    if (props?.fields?.recaptchaShown?.value && config.recaptchaPublicSiteKey) {
      const token = await (recaptchaRef?.current as unknown as ReCAPTCHA)?.executeAsync();
      const body_content = JSON.stringify({ token: token });
      const response = await fetch('/api/verify-recaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body_content,
      });

      const data = await response.json();
      if (!data.success) {
        newErrors.verifyReCaptcha =
          props?.fields?.recaptchaFailedLabelMessage?.value || 'ReCaptcha token validation failed.';
      }

      (recaptchaRef?.current as unknown as ReCAPTCHA)?.reset();
    }

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      const firstErrorField = form.querySelector(`[name="${Object.keys(newErrors)[0]}"]`);
      firstErrorField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setFormErrors({});

    let toEmail = props?.fields?.emailTo?.value;
    if (
      formData['Subject']?.toString().toUpperCase().includes('B2B') &&
      props?.fields?.emailB2B?.value
    ) {
      toEmail = props?.fields?.emailB2B?.value;
    }

    let emailSubject = 'Contact Form Submission';
    if (formData['Subject']) emailSubject += ' ' + formData['Subject'];
    if (formData['Regarding']) emailSubject += ' - ' + formData['Regarding'];

    let emailMsg = 'From Address: ' + props?.fields?.emailFrom?.value + '\r\n';
    emailMsg += 'From Email Id: ' + props?.fields?.emailFrom?.value + '\r\n';
    emailMsg += 'Received: ' + getFormattedDateTime() + '\r\n';
    emailMsg += 'To Addresses: ' + toEmail + '\r\n';
    emailMsg += 'CC Addresses: ' + props?.fields?.emailCC?.value + '\r\n';
    emailMsg += 'Subject: ' + emailSubject + '\r\n';
    for (const [name, _label] of Object.entries(nameLabelMappings)) {
      let sep = '';
      let emailLabel = name;
      if (emailLabel === 'Comments') {
        sep = '\r\n';
      } else if (emailLabel === 'Subject' || emailLabel === 'Drain_Type') {
        continue;
      } else if (emailLabel === 'State_Province') {
        emailLabel = 'State/Province';
      } else if (emailLabel === 'Zip_Postal_Code') {
        emailLabel = 'Zip/Postal Code';
      }
      emailLabel = emailLabel.replace('_', ' ');

      let formValue = formData[name];
      if (name === 'Read_Terms' || name == 'Permission') {
        formValue = formValue ? 'Yes' : 'No';
      }

      if (name === 'Read_Terms') {
        emailMsg += 'Webform: ' + props?.fields?.emailWebformName?.value + '\r\n';
        emailMsg += 'Website: ' + currentUrl + '\r\n';
      } else if (name === 'Regarding' && formData['Drain_Type']) {
        // replace regarding with drain type if it exists
        emailLabel = 'Drain Type';
        formValue = formData['Drain_Type'];
      }

      emailMsg += `${emailLabel}: ${formValue}${sep}\r\n`;
    }

    try {
      const response = await fetch('/api/ScjBrandsContact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: emailMsg,
          from: props?.fields?.emailFrom?.value,
          subject: emailSubject,
          to: toEmail,
          cc: props?.fields?.emailCC?.value,
        }),
      });

      const result = await response.json();

      if (result.success) {
        if (props.fields?.confirmationPage?.value?.href) {
          router.push(
            rewritePathByHost(props.fields?.confirmationPage?.value?.href, currentUrl) ?? ''
          );
        }
      } else {
        console.error('Email sending failed:', result.error);
      }
    } catch (err) {
      console.error('Error sending email:', err);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} noValidate className={base()}>
      <div className={formWrapper()}>
        <div className={formContainerTop()}>
          {props.fields?.formTitle?.value && (
            <div className={formHeader()}>
              <div className={horizontalBar()}></div>

              {props.fields?.formTitle?.value && (
                <Text tag="h2" field={{ value: props.fields?.formTitle?.value }} />
              )}

              {props.fields?.formIntroduction && (
                <div className={formIntroduction()}>
                  <RichTextA11yWrapper field={props.fields?.formIntroduction} />
                </div>
              )}

              {props.fields?.requiredFieldsLabel?.value && (
                <p className={requiredLabel()}>
                  <span className={requiredColor()}>* </span>
                  {props.fields?.requiredFieldsLabel?.value}
                </p>
              )}
            </div>
          )}
          <div className={formContainerOneColumn()}>
            <FormField
              name="Title"
              label={props.fields?.titleLabel?.value}
              required={props.fields?.titleRequired?.value}
              show={props.fields?.titleShown?.value}
              data={props.fields?.titleData}
              type="select"
              error={formErrors.Title}
              onBlur={handleBlur}
              onInput={handleInput}
            />
          </div>
          <div className={formContainerTwoColumn()}>
            <FormField
              name="First_Name"
              label={props.fields?.firstNameLabel?.value}
              required={props.fields?.firstNameRequired?.value}
              show={props.fields?.firstNameShown?.value}
              error={formErrors.First_Name}
              onBlur={handleBlur}
              onInput={handleInput}
            />
            <FormField
              name="Last_Name"
              label={props.fields?.lastNameLabel?.value}
              required={props.fields?.lastNameRequired?.value}
              show={props.fields?.lastNameShown?.value}
              error={formErrors.Last_Name}
              onBlur={handleBlur}
              onInput={handleInput}
            />
          </div>
          <div className={formContainerTwoColumn()}>
            <FormField
              name="Email"
              label={props.fields?.emailLabel?.value}
              required={props.fields?.emailRequired?.value}
              show={props.fields?.emailShown?.value}
              error={formErrors.Email}
              onBlur={handleBlur}
              onInput={handleInput}
            />
            <FormField
              name="Verify_Email"
              label={props.fields?.verifyEmailLabel?.value}
              required={props.fields?.verifyEmailRequired?.value}
              show={props.fields?.verifyEmailShown?.value}
              error={formErrors.Verify_Email}
              onBlur={handleBlur}
              onInput={handleInput}
            />
          </div>
          <div className={formContainerOneColumn()}>
            <FormField
              name="Phone"
              label={props.fields?.phoneLabel?.value}
              required={props.fields?.phoneRequired?.value}
              show={props.fields?.phoneShown?.value}
              error={formErrors.Phone}
              onBlur={handleBlur}
              onInput={handleInput}
            />
          </div>
          <div className={formContainerOneColumn()}>
            <FormField
              name="Address"
              label={props.fields?.addressLabel?.value}
              required={props.fields?.addressRequired?.value}
              show={props.fields?.addressShown?.value}
              error={formErrors.Address}
              onBlur={handleBlur}
              onInput={handleInput}
            />
          </div>
          <div className={formContainerOneColumn()}>
            <FormField
              name="Address2"
              label={props.fields?.addressApartmentLabel?.value}
              required={props.fields?.addressApartmentRequired?.value}
              show={props.fields?.addressApartmentShown?.value}
              error={formErrors.Address2}
              onBlur={handleBlur}
              onInput={handleInput}
            />
          </div>
          <div className={`${formContainerTwoColumn()} ${negativeSpace()}`}>
            <div className="flex flex-col">
              <FormField
                name="City"
                label={props.fields?.cityLabel?.value}
                required={props.fields?.cityRequired?.value}
                show={props.fields?.cityShown?.value}
                error={formErrors.City}
                onBlur={handleBlur}
                onInput={handleInput}
              />
            </div>
            <FormField
              name="State_Province"
              label={props.fields?.stateLabel?.value}
              required={props.fields?.stateRequired?.value}
              show={props.fields?.stateShown?.value}
              data={props.fields?.stateData}
              type={props.fields?.stateData?.length ? 'select' : 'text'}
              error={formErrors.State_Province}
              onBlur={handleBlur}
              onInput={handleInput}
            />
          </div>
          <div className={`${formContainerTwoColumn()} ${negativeSpace()}`}>
            <div className="flex flex-col">
              <FormField
                name="Zip_Postal_Code"
                label={props.fields?.zipLabel?.value}
                required={props.fields?.zipRequired?.value}
                show={props.fields?.zipShown?.value}
                error={formErrors.Zip_Postal_Code}
                onBlur={handleBlur}
                onInput={handleInput}
              />
            </div>
            <FormField
              name="Country"
              label={props.fields?.countryLabel?.value}
              required={props.fields?.countryRequired?.value}
              show={props.fields?.countryShown?.value}
              data={props.fields?.countryData}
              type="select"
              error={formErrors.Country}
              onBlur={handleBlur}
              onInput={handleInput}
            />
          </div>
        </div>
        <div className={formContainerBottom()}>
          {props.fields?.formTitle?.value && (
            <div className={formHeader()}>
              <div className={horizontalBar()}></div>

              {props.fields?.formTitle?.value && (
                <Text tag="h2" field={{ value: props.fields?.fromSecondaryTItle?.value }} />
              )}
            </div>
          )}
          <div className={formContainerOneColumn()}>
            <FormField
              name="Subject"
              label={props.fields?.subjectLabel?.value}
              required={props.fields?.subjectRequired?.value}
              show={props.fields?.subjectShown?.value}
              data={props.fields?.subjectData}
              type="select"
              error={formErrors.Subject}
              onBlur={handleBlur}
              onInput={handleInput}
            />
          </div>
          <div className={formContainerOneColumn()}>
            <FormField
              name="Regarding"
              label={props.fields?.regardingLabel?.value}
              required={props.fields?.regardingRequired?.value}
              show={props.fields?.regardingShown?.value}
              data={props.fields?.regardingData}
              type="select"
              error={formErrors.Regarding}
              onBlur={handleBlur}
              onInput={handleInput}
            />
          </div>
          {regardingValue ===
            props.fields?.regardingSecondaryShowOnRegardingSelection?.fields?.value?.value && (
            <div className={formContainerOneColumn()}>
              <FormField
                name="Drain_Type"
                label={props.fields?.regardingSecondaryLabel?.value}
                required={props.fields?.regardingSecondaryRequired?.value}
                show={props.fields?.regardingSecondaryShown?.value}
                data={props.fields?.regardingSecondaryData}
                type="select"
                error={formErrors.Drain_Type}
                onBlur={handleBlur}
                onInput={handleInput}
              />
            </div>
          )}
          <div className={formContainerTwoColumn()}>
            <FormField
              name="UPC"
              label={props.fields?.productUpcLabel?.value}
              required={
                props.fields?.productUpcRequired?.value ||
                subjectValue ===
                  props.fields?.productUpcMakeRequiredOnSubjectValue?.fields?.value?.value
              }
              show={props.fields?.productUpcShown?.value}
              error={formErrors.UPC}
              onBlur={handleBlur}
              onInput={handleInput}
            />
            {upcHelpText?.value && (
              <span className={UpcHelpImage()}>
                <GoogleMaterialSymbol icon="infoIcon" />

                <div className={helpBoxRTE()}>
                  <RichTextA11yWrapper field={upcHelpText} />
                </div>
              </span>
            )}
            <div className="md:z-10">
              <FormField
                name="Batch_Code"
                label={props.fields?.batchCodeLabel?.value}
                required={props.fields?.batchCodeRequired?.value}
                show={props.fields?.batchCodeShown?.value}
                error={formErrors.Batch_Code}
                onBlur={handleBlur}
                onInput={handleInput}
              />
            </div>
            {batchHelpText?.value && (
              <span className={BatchHelpImage()}>
                <GoogleMaterialSymbol icon="infoIcon" />
                <div className={helpBoxRTE()}>
                  <RichTextA11yWrapper field={batchHelpText} />
                </div>
              </span>
            )}
          </div>
          <div className={formContainerOneColumn()}>
            <FormField
              name="Comments"
              label={props.fields?.commentLabel?.value}
              required={props.fields?.commentRequired?.value}
              show={props.fields?.commentShown?.value}
              type="textarea"
              error={formErrors.Comments}
              onBlur={handleBlur}
              onInput={handleInput}
            />
          </div>
          <FormField
            name="Permission"
            label={props.fields?.contactConsentLabel?.value}
            required={props.fields?.contactConsentRequired?.value}
            show={props.fields?.contactConsentShown?.value}
            type="checkbox"
            error={formErrors.Permission}
            onBlur={handleBlur}
            onInput={handleInput}
          />
          <FormField
            name="Read_Terms"
            label={props.fields?.termsAndPrivacyConsentLabel?.value}
            required={props.fields?.termsAndPrivacyConsentRequired?.value}
            show={props.fields?.termsAndPrivacyConsentShown?.value}
            type="checkbox"
            error={formErrors.Read_Terms}
            onBlur={handleBlur}
            onInput={handleInput}
          />

          <RichTextA11yWrapper field={props.fields?.formNote} />

          <button
            type="submit"
            className={`${submitButton()} ${!isValid && submitButtonInvalid()}`}
            disabled={!isValid}
          >
            {props?.fields?.submitButtonLabel?.value}
          </button>
          {props?.fields?.recaptchaShown?.value && config.recaptchaPublicSiteKey && (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={config.recaptchaPublicSiteKey}
              hl={current_lang}
              size="invisible"
            />
          )}
          {formErrors.verifyReCaptcha && (
            <p className={errorMessageText()}>{formErrors.verifyReCaptcha}</p>
          )}
        </div>
      </div>
    </form>
  );
};

export default SCJBrandsContactForm;
